'use client';

/**
 * VIDEO LAB — round-1 "cards mode" (Control Room v4 §VIDEO LAB · VSL SEGMENTS),
 * pixel-matched via SpecCard. Real data, same APIs as classic /funnels/video-lab:
 *   GET /api/funnels/<funnelId>/stats                → stats.videos.videos (per-video engagement)
 *   GET /api/funnels/videolab/transcribe             → { labs: [{lab, segments, hasProposal, hasOutput}] }
 *   GET /api/funnels/<funnelId>/retention?videoId=…  → per-second retention curve
 * Heavy edit flow (upload → transcribe → propose → render → deploy) stays in
 * classic until the P7 flip; cards deep-link into it.
 */
import { useEffect, useRef, useState } from 'react';
import type { BusinessProject } from '@/lib/business-projects';
import { Glass, SpecCard, StatusChip, EmptyState, SC, mono } from '@/future/primitives';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function VideoLabSurface({ project }: { project: BusinessProject | null }) {
  const [labs, setLabs] = useState<any[] | null>(null);
  const [videos, setVideos] = useState<any[]>([]);
  const [ret, setRet] = useState<any>(null);
  const [hover, setHover] = useState<{ t: number; pct: number; x: number } | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/funnels/videolab/transcribe').then(r => r.json())
      .then(d => setLabs(d.labs || [])).catch(() => setLabs([]));
  }, []);

  useEffect(() => {
    if (!project?.funnelId) return;
    let live = true;
    const load = () => fetch(`/api/funnels/${project.funnelId}/stats?range=30d`).then(r => r.json())
      .then(d => { if (live) setVideos(d?.stats?.videos?.videos || []); }).catch(() => {});
    load();
    const i = setInterval(load, 60_000); // live-stats law: 60s
    fetch(`/api/funnels/${project.funnelId}/retention?videoId=main-vsl&bucketSeconds=1`)
      .then(r => r.json()).then(d => { if (live && d?.retention) setRet(d); }).catch(() => {});
    return () => { live = false; clearInterval(i); };
  }, [project?.funnelId]);

  return (
    <div className="max-w-[1240px] mx-auto space-y-5">
      {/* VSL segment cards — design: cards wrap, gap 16 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignContent: 'flex-start' }}>
        {videos.map((v: any) => {
          const under = (v.avgCompletion || 0) < 50;
          return (
            <SpecCard key={`${v.videoPage}-${v.videoId}`}
              title={v.videoId} sub={`/${v.videoPage} · ${v.totalViews} VIEWS · ${v.uniqueViewers}U`}
              big={`${Math.round(v.avgCompletion ?? 0)}%`} delta="COMPLETION"
              w={Math.min(100, v.avgCompletion || 0)}
              foot={under ? 'RECUT SUGGESTED' : 'LIVE ON FUNNEL'}
              badge={under ? 'REVIEW' : 'LIVE'} badgeHex={under ? SC.amber : SC.green} />
          );
        })}
        {(labs || []).map((l: any) => (
          <SpecCard key={l.lab}
            title={l.lab} sub={`${l.segments} SEGMENTS${l.hasProposal ? ' · AI PLAN READY' : ''}`}
            big={l.hasOutput ? 'DONE' : l.hasProposal ? 'PLAN' : 'RAW'}
            delta={l.hasOutput ? 'RENDERED' : l.hasProposal ? 'AWAITING RENDER' : 'TRANSCRIBED'}
            w={l.hasOutput ? 100 : l.hasProposal ? 55 : 25}
            foot="OPEN IN EDITOR →"
            badge={l.hasOutput ? 'DONE' : l.hasProposal ? 'REVIEW' : 'QUEUED'}
            badgeHex={l.hasOutput ? SC.green : SC.amber}
            href={`/funnels/video-lab?lab=${l.lab}`} />
        ))}
      </div>
      {labs !== null && labs.length === 0 && videos.length === 0 && (
        <EmptyState what="VIDEO LAB EMPTY" action="Upload a VSL in the editor to start — /funnels/video-lab." />
      )}
      {labs === null && <div className="py-10 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>SCANNING LABS…</div>}

      {/* live VSL retention — per-second dropOffCurve (classic coverage: retention chart + cliffs) */}
      {ret && (() => {
        const c = ret.retention as { t: number; pct: number }[];
        const dur = ret.durationSeconds || c[c.length - 1]?.t || 1;
        const pts = c.map(p => `${(p.t / dur) * 100},${40 - (p.pct / 100) * 38}`).join(' ');
        const drops: any[] = [];
        for (let i = 10; i < c.length; i++) drops.push({ t: c[i].t, d: c[i - 10].pct - c[i].pct, p: c[i].pct });
        const cliffs = drops.sort((a, b) => b.d - a.d).slice(0, 3);
        const timeAt = (clientX: number) => {
          const r = chartRef.current!.getBoundingClientRect();
          const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
          const t = Math.round(frac * dur);
          const pt = c.reduce((a, b) => Math.abs(b.t - t) < Math.abs(a.t - t) ? b : a, c[0]);
          return { t, pct: pt.pct, x: frac * 100 };
        };
        return (
          <Glass className="p-5" style={{ borderRadius: 14 }}>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <span style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>
                LIVE VSL RETENTION · {String(ret.videoId).toUpperCase()} · {fmt(dur)} · {ret.totalViews} VIEWS
              </span>
              <span style={{ ...mono(13, '.14em'), color: 'var(--uif-t6)' }}>PER SECOND · SOURCE: FUNNEL /stats/videos/retention</span>
            </div>
            <div ref={chartRef} className="relative select-none" style={{ cursor: 'crosshair' }}
              onMouseMove={e => setHover(timeAt(e.clientX))} onMouseLeave={() => setHover(null)}>
              <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-36 block">
                {[25, 50, 75].map(g => (
                  <line key={g} x1="0" x2="100" y1={40 - (g / 100) * 38} y2={40 - (g / 100) * 38}
                    stroke="rgba(125,232,255,.07)" strokeWidth="0.2" />
                ))}
                {Array.from({ length: Math.floor(dur / 60) }, (_, i) => (i + 1) * 60).map(t => (
                  <line key={t} x1={(t / dur) * 100} x2={(t / dur) * 100} y1="0" y2="40"
                    stroke="rgba(125,232,255,.05)" strokeWidth="0.15" />
                ))}
                <polygon points={`0,40 ${pts} 100,40`} fill="url(#uifRetGrad)" />
                <polyline points={pts} fill="none" stroke="rgba(158,240,255,.85)" strokeWidth="0.45"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                {hover && <line x1={hover.x} x2={hover.x} y1="0" y2="40" stroke="rgba(168,238,255,.7)" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />}
                <defs>
                  <linearGradient id="uifRetGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(125,232,255,.22)" />
                    <stop offset="100%" stopColor="rgba(125,232,255,.02)" />
                  </linearGradient>
                </defs>
              </svg>
              {hover && (
                <div className="absolute -top-1 px-2 py-1 rounded pointer-events-none whitespace-nowrap"
                  style={{ left: `${hover.x}%`, transform: hover.x > 80 ? 'translateX(-105%)' : 'translateX(8px)',
                    background: 'rgba(4,10,16,.92)', border: '1px solid rgba(125,232,255,.3)',
                    ...mono(15, '.08em'), color: 'var(--uif-t2)' }}>
                  {fmt(hover.t)} · {hover.pct.toFixed(1)}% WATCHING
                </div>
              )}
            </div>
            <div className="relative h-4 mt-1" style={{ ...mono(13, '.08em'), color: 'var(--uif-t6)' }}>
              {Array.from({ length: Math.floor(dur / 120) + 1 }, (_, i) => i * 120).map(t => (
                <span key={t} className="absolute -translate-x-1/2" style={{ left: `${(t / dur) * 100}%` }}>{fmt(t)}</span>
              ))}
              <span className="absolute right-0">{fmt(dur)}</span>
            </div>
            {cliffs.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {cliffs.map(x => (
                  <StatusChip key={x.t} kind="bad"
                    label={`CLIFF @ ${fmt(x.t)} · −${x.d.toFixed(1)}PP → ${x.p.toFixed(1)}% LEFT`} />
                ))}
              </div>
            )}
          </Glass>
        );
      })()}
    </div>
  );
}
